import { LOG_API_CALL_DURATION_ENABLE } from '@aibees/elephant/constants';
import {
  useSearchQueryFilter,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { Grid2 } from '@aibees/shared/components';
import { classnames, peformanceLogTime } from '@aibees/shared/utils';
import { Box, Radio, RadioGroup, RadioGroupProps } from '@mui/material';
import React, { memo, useMemo } from 'react';
import { RadioFilterLabel } from './components/radio-filter-label';
import * as SX from './sx';
import type { RadioFilterProps as Props } from './types';

export const RadioFilter = memo(
  ({ className, open, metadata, field, ...filter }: Props) => {
    const { params, setParam, removeParam } = useSearchQueryFilter({
      select: [field!],
    });
    const searchType = useSearchTypeFromRoute();
    const { track } = useTrackEvents();
    const selectedValue = useMemo(
      () =>
        params[field!] ?? filter.options?.find((item) => item.selected)?.value,
      [params, field, filter.options]
    );

    const handleChange: RadioGroupProps['onChange'] = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (LOG_API_CALL_DURATION_ENABLE)
        peformanceLogTime(`filter change start: ${field}`);

      if (event.target.value === 'all') {
        removeParam(field!);
      } else {
        setParam(field!, event.target.value);
        track('FILTER', {
          filterName: field,
          filterType: filter.type,
          filterValue: event.target.value,
          searchType,
        });
      }
      if (LOG_API_CALL_DURATION_ENABLE)
        peformanceLogTime(`filter change end: ${field}`);
    };

    return (
      <Box
        className={classnames('RadioFilter', className)}
        display="flex"
        flexDirection="column"
        gap={1}
        sx={SX.root}
      >
        <RadioGroup
          onChange={handleChange}
          value={selectedValue ?? ''}
          sx={metadata?.styles?.sx}
        >
          {filter.options?.map((item) => {
            return (
              <Grid2
                key={item.value}
                display="flex"
                flexDirection="column"
                width="100%"
                paddingBottom={1}
              >
                <RadioFilterLabel value={item.label}>
                  <Radio
                    value={item.value}
                    name={field}
                    checked={selectedValue === item.value}
                    size="small"
                  />
                </RadioFilterLabel>
              </Grid2>
            );
          })}
        </RadioGroup>
      </Box>
    );
  }
);

RadioFilter.displayName = 'RadioFilter';
